import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Links from '../lib/links';
import { VehicleDetails } from '../types';
import { source, vehicleContext } from '../components/VehicleProvider';
import useStaticVehicleData from './useStaticVehicleData';
import { IS_STAGING_PAGE } from '../constants/environment';

interface CTAUrls {
  bookATestDriveUrl?: string;
  contactDealerUrl?: string;
  purchaseMethodUrl?: string;
  requestAQuoteUrl?: string;
  howEDepositWorksUrl?: string;
  paymentCalculatorUrl?: string;
}
type UseCTAUrl = () => CTAUrls;

// TODO FIXME: CTA url handling

const useCTAUrl: UseCTAUrl = () => {
  const { i18n } = useTranslation();
  const { vehicle, market } = useContext(vehicleContext);
  const lang = i18n.languages?.[0] || 'en';
  const {
    audiCode,
    modelID,
    dealerId,
    stockNumber,
    modelMake,
    modelYear,
    modelName,
    id,
    isAvailableNow,
    vehicleType,
  } = (vehicle as VehicleDetails) ?? {};

  // @ts-ignore
  const windowVisitor = typeof window !== 'undefined' ? window?.ss?.visitor : undefined;
  const mcvid = windowVisitor ? windowVisitor?.getMarketingCloudVisitorID() : undefined;
  const mcvidParam = mcvid ? `&mcvid=${mcvid}` : '';

  const nonAudi = vehicleType === 'non-audi';

  // account for new carline ID format
  const [modelId] = modelID?.split('-') ?? [];

  const staticVehicle = useStaticVehicleData(modelId);
  const nonAudiModelName = useMemo(() => {
    if (modelName && modelName.split(' ').length > 3)
      return modelName.split(' ').slice(1).slice(-2).join(' ');

    if (modelName) return modelName.split(' ').pop();

    return undefined;
  }, [modelName]);
  const isCanadian = useMemo(() => market === 'ca' || market === 'cauc', [market]);

  const rapidName = staticVehicle ?? '';

  const contactDealer = Links.contactDealer.url;
  const testDrive = Links.testDrive.url;
  const tradeInUrl = Links.tradeIn.url;
  const audiUrl = 'https://www.audi.ca/ca/web/';

  const config = useMemo(() => {
    if (market === 'ca')
      return {
        type: 'ca',
        slug: 'nc',
        carcode: 'newcarcode',
      };
    return {
      type: 'cauc',
      slug: 'uc',
      carcode: 'preownedcarcode',
    };
  }, [market]);

  // Book A Test Drive
  // Use a different url if it's a used vehicle

  const bookATestDriveUrl = useMemo(() => {
    if (!vehicleType) return undefined;
    const usedVehicleBookATestDriveUrl =
      lang === 'en' ? Links?.formsUsedBookATestDrive?.en : Links?.formsUsedBookATestDrive?.fr;

    if (nonAudi) {
      if (!usedVehicleBookATestDriveUrl || nonAudiModelName) return undefined;
      return `${usedVehicleBookATestDriveUrl}?modelMake=${modelMake}&modelName=${nonAudiModelName}&modelYear=${modelYear}&dealer_id=49${dealerId}&vehicleId=${id}`.replace(
        /\s/g,
        '%20',
      );
    }

    if (config.type === 'cauc') {
      if (!usedVehicleBookATestDriveUrl) return undefined;
      return `${usedVehicleBookATestDriveUrl}?modelid=${rapidName}&dealer_id=49${dealerId}&modelYear=${modelYear}&stock_number=${stockNumber}&vehicleId=${id}`;
    }

    return `${audiUrl}${lang}/forms${testDrive}?modelid=${rapidName}&dealer_id=49${dealerId}&modelYear=${modelYear}&stock_number=${stockNumber}&vehicleId=${id}`.replace(
      /\s/g,
      '%20',
    );
  }, [
    vehicleType,
    lang,
    nonAudi,
    config.type,
    testDrive,
    rapidName,
    dealerId,
    modelYear,
    stockNumber,
    id,
    nonAudiModelName,
    modelMake,
  ]);

  // Place E-Deposit
  // TODO FIXME check if it's dead code
  const purchaseMethodUrl = useMemo(() => {
    if (!vehicleType) return undefined;
    return `${audiUrl}${lang}/forms${tradeInUrl}?market=${config?.type}&id=${modelId}`;
  }, [vehicleType, audiUrl, lang, tradeInUrl, config, modelId]);

  // Contact Dealer

  const contactDealerSource = useMemo(() => {
    const documentDefined = typeof window !== 'undefined' ? window?.document : undefined;
    if (documentDefined?.referrer.includes('match') || source === 'vtp-findyourmatch')
      return 'findyourmatch';
    if (market === 'ca' && !isAvailableNow) return `${config?.slug}-in-transit`;
    return config?.slug;
  }, [config?.slug, isAvailableNow, market]);

  const contactDealerUrl = useMemo(() => {
    if (!vehicleType) return undefined;
    if (nonAudi) {
      if (!nonAudiModelName) return undefined;

      return `${audiUrl}${lang}/forms${contactDealer}?modelMake=${modelMake}&modelName=${nonAudiModelName}&modelYear=${modelYear}&vehicleId=${id}&dealer_id=49${dealerId}${mcvidParam}`.replace(
        /\s/g,
        '%20',
      );
    }

    return `${audiUrl}${lang}/forms/contact-dealer-${config?.slug}.html?modelid=${rapidName}&dealer_id=49${dealerId}&stock_number=${stockNumber}&vehicleId=${id}&audi_code=${audiCode}&source=vtp-${contactDealerSource}${mcvidParam}`;
  }, [
    vehicleType,
    nonAudi,
    lang,
    config?.slug,
    rapidName,
    dealerId,
    stockNumber,
    audiCode,
    contactDealerSource,
    mcvidParam,
    nonAudiModelName,
    contactDealer,
    modelMake,
    modelYear,
    id,
  ]);

  // Request A Quote
  const requestAQuoteUrl = useMemo(() => {
    if (!vehicleType) return undefined;

    const isNew = vehicleType === 'new';

    return `https://${IS_STAGING_PAGE ? 'qa' : 'www'}.audiusa.com/us/web/en/shopping-tools/${
      isNew ? 'new' : 'used'
    }-inventory/layer/contact-dealer${
      IS_STAGING_PAGE ? '-dev' : ''
    }.html?code=${audiCode}&vehicleid=${id}${mcvidParam}${isNew ? '' : '&type=used'}`;
  }, [vehicleType, audiCode, id, mcvidParam]);

  // How e-deposit works
  const howEDepositWorksUrl = useMemo(() => {
    return `#layer=/${market}/web/${lang}/layer/how-e-deposit-works.html?audiCode=${audiCode}&vehicleId=${id}&dealerId=${dealerId}`;
  }, [market, lang, audiCode, id, dealerId]);

  // Payment Calculator URL
  const paymentCalculatorUrl = useMemo(() => {
    const protocol = 'https://';
    const usSubDomain = IS_STAGING_PAGE ? 'qa' : 'www';
    const caSubDomain = IS_STAGING_PAGE ? 'pre-www' : 'www';
    const subdomain = market === 'ca' ? caSubDomain : usSubDomain;
    const domain = market === 'ca' ? 'audi.ca' : 'audiusa.com';
    const path = IS_STAGING_PAGE ? 'test_pages/' : '';
    const basePath = IS_STAGING_PAGE ? `${market}/web/${lang}/` : `${lang}/`;
    const page = IS_STAGING_PAGE ? 'online-shopping.html' : 'online-shopping/';
    const queryParams = `?market=${market}&vehicleId=${id}`;
    return `${protocol}${subdomain}.${domain}/${basePath}${path}${page}${queryParams}`;
  }, [market, id, lang]);

  return {
    bookATestDriveUrl,
    contactDealerUrl,
    purchaseMethodUrl,
    requestAQuoteUrl,
    howEDepositWorksUrl,
    paymentCalculatorUrl,
  };
};

export default useCTAUrl;
